import { APP_CONFIG }       from '@appConfig/_app-config_master';
import { APP_MODE }         from '@appConfig/app-mode-config';
import { KEYCLOACK_CONFIG } from '@appConfig/keycloak-config';
import { environment }      from '@env/environment';
import {
	KeycloakOptions,
	KeycloakService
}                           from 'keycloak-angular';

export {
	deeplinkSessionStorageGet,
	deeplinkSessionStorageSet,
	getDeepLinkUri,
	getRedirectUri,
	initializer,
	isInitializedAndRedirected
};

const CALLBACK_URI = 'callback';
const DEEP_LINK_SESSION_STORAGE_ID = 'deeplink';

/**
 * stores deeplink encoded to sessionStorage
 */
function deeplinkSessionStorageSet(): void {
	window.sessionStorage.setItem( DEEP_LINK_SESSION_STORAGE_ID, encodeURIComponent( getDeepLinkUri() ) );
}

/**
 * reads deeplink from sessionStorage
 * @options
 *  readAndDelete: reads and removes deeplink from sessionStorage | default: false
 *  decodeURIComponent: returns decoded deeplink from sessionStorage | default: false
 */
function deeplinkSessionStorageGet( options?: { readAndDelete?: boolean, decodeURIComponent?: boolean } ): string {
	const deeplink: string = window.sessionStorage.getItem( DEEP_LINK_SESSION_STORAGE_ID );
	if ( options?.readAndDelete ) { window.sessionStorage.removeItem( DEEP_LINK_SESSION_STORAGE_ID ); }

	return options?.decodeURIComponent
				 ? decodeURIComponent( deeplink )
				 : deeplink;
}

/**
 * Extracts deeplink url string part from window.location.pathname
 * Beside the deeplink path window.location.pathname can contain PortalBaseUrl and CallbackUrl segments
 */
function getDeepLinkUri(): string {
	return window.location.pathname
	.replace( getPortalBaseUrl(), '' )
	.replace( CALLBACK_URI, '' )
	.substring( 1 );
}

/**
 * Extracts portalBaseUrl string part from window.location.pathname
 * Pattern of portalBaseUrl is: /api/{{ optional realm }}/management-ui
 */
function getPortalBaseUrl(): string {
	let portalBaseUrl = '';

	const portalBaseMatchArray = window.location.pathname.match( KEYCLOACK_CONFIG.portalBaseUrlSegmentRegex );

	if ( Array.isArray( portalBaseMatchArray ) ) { portalBaseUrl = portalBaseMatchArray[ 0 ]; }
	return portalBaseUrl;
}

/**
 * Returns RedirectUri
 * Pattern of redirectUri is: {{ window.location.origin }}/{{ portalBaseUrl }}/{{ CALLBACK_URI }}
 */
function getRedirectUri( options?: { encodeURI?: boolean } ): string {
	const redirectUrl = `${ window.location.origin }${ getPortalBaseUrl() }/${ CALLBACK_URI }`;

	return options?.encodeURI
				 ? encodeURIComponent( redirectUrl )
				 : redirectUrl;
}

/**
 * Initializes Keycloak
 */
function initializer( keycloak: KeycloakService ): () => Promise<any> {
	if ( !isInitializedAndRedirected() ) {
		deeplinkSessionStorageSet();
	}

	let options: KeycloakOptions = {
		config: environment.keycloak,
		initOptions: {
			onLoad: 'login-required',
			checkLoginIframe: false,
			pkceMethod: 'S256',
			enableLogging: [ APP_MODE.DEVELOP, APP_MODE.LOCAL ].includes( APP_CONFIG.appMode ),
			redirectUri: !isInitializedAndRedirected()
									 ? getRedirectUri()
									 : null
		},
		enableBearerInterceptor: true,
		loadUserProfileAtStartUp: true,
		bearerExcludedUrls: [ '/assets', '/styles' ]
	};

	if ( !KEYCLOACK_CONFIG.useKeycloakService ) {
		options = {
			...options,
			config: {
				url: null,
				realm: null,
				clientId: null
			},
			initOptions: {},
			enableBearerInterceptor: false,
			loadUserProfileAtStartUp: false
		};
	}

	return (): Promise<any> => keycloak.init( options )
	.catch( ( error ) => {
		console.log( 'keycloakInit error: ', error );
		return Promise.resolve();
	} );
}

/**
 * Checks if keycloak has been initialized and redirected to callback url
 * Check is true if window.location.pathname === {{} portalBaseUrl }/{{ CALLBACK_URI }}
 */
function isInitializedAndRedirected(): boolean {
	return window.location.pathname === `${ getPortalBaseUrl() }/${ CALLBACK_URI }`;
}
